/**
 * PlayerStatsCard
 * Renders a rich player stats card with season averages, stat selector pills,
 * window toggles (L5/L10/L20), and a recharts bar chart of recent games.
 */

import type { PlayerStatsCardData } from '@parlay-picker/shared/chatTypes';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { shortStatLabel } from '@parlay-picker/shared/statLabels';
import {
  computeNiceTicks,
  gameChartLabel,
  preseasonWindowNote,
} from '@parlay-picker/shared/statsChart';
import { getTeamAbbr } from '@parlay-picker/shared/teams';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatStatEntries, PlayerStatsHeader } from './cardHelpers';

export function PlayerStatsCard({ data }: { data: PlayerStatsCardData }): ReactElement {
  const name = data.playerName || 'Unknown';
  const team = data.team || '';
  const l5 = data.l5;
  const l10 = data.l10;
  const l20 = data.l20;
  const season = data.season;
  const stats = season || l5;
  const recentGames = data.recentGames;
  // Conversations persist for 7 days, and cards saved before leagueKey was sent lack it.
  // Those abbreviate with no league rather than in a guessed one.
  const leagueKey: LeagueKey | undefined = data.leagueKey;

  const statOptions = ['pts', 'reb', 'ast', 'fg3m', 'stl', 'blk'];
  const [selectedStat, setSelectedStat] = useState('pts');
  const [selectedWindow, setSelectedWindow] = useState<'l5' | 'l10' | 'l20'>('l5');

  const windowMap = { l5: l5, l10: l10, l20: l20 };
  const windowGames = {
    l5: recentGames?.slice(0, 5),
    l10: recentGames?.slice(0, 10),
    l20: recentGames?.slice(0, 20),
  };
  const gamesToShow = windowGames[selectedWindow] || [];
  const avg = windowMap[selectedWindow]?.[selectedStat] ?? season?.[selectedStat];
  const hasChartData = gamesToShow.length >= 3;

  const chartData = useMemo(
    () =>
      gamesToShow.map((game) => ({
        opponent: gameChartLabel(
          game.opponent ? getTeamAbbr(String(game.opponent), leagueKey) : '?',
          game.isPreseason,
        ),
        value: Number(game[selectedStat] || 0),
        dnp: Boolean(game.dnp),
      })),
    [gamesToShow, selectedStat, leagueKey],
  );

  const { ticks: yTicks, maxValue: yMax } = useMemo(() => {
    const dataMax = Math.max(...chartData.map((d) => d.value), avg ?? 0, 1);
    return computeNiceTicks(0, dataMax);
  }, [chartData, avg]);

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-4 space-y-3.5">
      <PlayerStatsHeader name={name} team={team} leagueKey={leagueKey} />
      {data.preseasonWindow && (
        <p className="text-[10px] text-theme-text-tertiary">
          {preseasonWindowNote(data.preseasonWindow)}
        </p>
      )}

      {/* Season stats — compact 2x3 grid */}
      {stats && (
        <div className="grid grid-cols-3 gap-x-4 gap-y-2">
          {formatStatEntries(stats)
            .filter(([key]) => !['games', 'to'].includes(key.toLowerCase()))
            .slice(0, 6)
            .map(([key, value]) => (
              <div key={key} className="text-center">
                <div className="text-[9px] text-theme-text-tertiary uppercase">{key}</div>
                <div className="text-sm font-semibold text-theme-text">{value}</div>
              </div>
            ))}
        </div>
      )}

      {/* Interactive chart section */}
      {hasChartData && (
        <div className="border-t border-theme-border/50 pt-2 space-y-1.5">
          {/* Stat + Window selectors on one row */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-1 flex-wrap">
              {statOptions.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setSelectedStat(s)}
                  className={`text-[9px] px-1.5 py-0.5 rounded-full transition-colors ${
                    selectedStat === s
                      ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                      : 'bg-theme-bg text-theme-text-secondary hover:bg-theme-surface-hover'
                  }`}
                >
                  {shortStatLabel(s)}
                </button>
              ))}
            </div>
            <div className="flex gap-1 shrink-0">
              {(['l5', 'l10', 'l20'] as const).map((w) => (
                <button
                  type="button"
                  key={w}
                  onClick={() => setSelectedWindow(w)}
                  className={`text-[9px] px-1.5 py-0.5 rounded transition-colors ${
                    selectedWindow === w
                      ? 'bg-theme-primary/20 text-theme-primary font-medium'
                      : 'text-theme-text-tertiary hover:text-theme-text-secondary'
                  }`}
                >
                  {w.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Recharts bar chart */}
          <ResponsiveContainer width="100%" height={130}>
            <BarChart data={chartData} margin={{ top: 14, right: 4, bottom: 0, left: -16 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border, #333)"
                opacity={0.3}
                vertical={false}
              />
              <XAxis
                dataKey="opponent"
                tick={{ fontSize: 9, fill: 'var(--color-text-tertiary, #888)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 8, fill: 'var(--color-text-tertiary, #888)' }}
                axisLine={false}
                tickLine={false}
                width={28}
                domain={[0, yMax]}
                ticks={yTicks}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-surface, #1a1a1a)',
                  border: '1px solid var(--color-border, #333)',
                  borderRadius: 8,
                  fontSize: 11,
                  padding: '4px 8px',
                }}
                labelStyle={{ color: 'var(--color-text, #fff)', fontWeight: 600 }}
                itemStyle={{ color: 'var(--color-text-secondary, #aaa)' }}
                formatter={(value) => [`${value} ${shortStatLabel(selectedStat)}`, '']}
                labelFormatter={(label) => `vs ${label}`}
              />
              {avg != null && (
                <ReferenceLine
                  y={avg}
                  stroke="var(--color-primary, #6366f1)"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{
                    value: `avg ${typeof avg === 'number' ? avg.toFixed(1) : avg}`,
                    position: 'insideTopRight',
                    fontSize: 8,
                    fill: 'var(--color-primary, #6366f1)',
                    offset: 2,
                  }}
                />
              )}
              <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={36}>
                <LabelList
                  dataKey="value"
                  position="top"
                  fontSize={8}
                  fill="var(--color-text-secondary, #aaa)"
                  fontWeight={600}
                />
                {chartData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={
                      entry.dnp
                        ? 'var(--color-text-tertiary, #555)'
                        : avg != null && entry.value >= avg
                          ? 'var(--color-success, #22c55e)'
                          : 'var(--color-danger, #ef4444)'
                    }
                    fillOpacity={entry.dnp ? 0.3 : 0.85}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Quick action buttons */}
      <div className="flex items-center gap-1.5 pt-1 border-t border-theme-border/50">
        <button
          type="button"
          onClick={() => {
            useGlobalShortcutsStore.getState().openQuickStats();
            window.dispatchEvent(
              new CustomEvent('chat-prefill-quick-stats', { detail: { playerName: name } }),
            );
          }}
          className="text-[10px] px-2 py-1 rounded-lg bg-theme-bg border border-theme-border hover:bg-theme-surface-hover text-theme-text-secondary transition flex items-center gap-1"
        >
          Quick Stats
        </button>
        <button
          type="button"
          onClick={() => {
            useGlobalShortcutsStore.getState().openRosterIntel();
            if (team) {
              window.dispatchEvent(
                new CustomEvent('chat-prefill-roster-intel', { detail: { team } }),
              );
            }
          }}
          className="text-[10px] px-2 py-1 rounded-lg bg-theme-bg border border-theme-border hover:bg-theme-surface-hover text-theme-text-secondary transition flex items-center gap-1"
        >
          Roster Intel
        </button>
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(
              new CustomEvent('chat-navigate-tab', { detail: { tab: 'picks' } }),
            );
          }}
          className="text-[10px] px-2 py-1 rounded-lg bg-theme-bg border border-theme-border hover:bg-theme-surface-hover text-theme-text-secondary transition flex items-center gap-1"
        >
          Add to Builder
        </button>
      </div>
    </div>
  );
}
